import { ScanLine, Check, Sparkles, LayoutList, Tag, UtensilsCrossed } from "lucide-react";
import { t, lang } from "@/i18n";

const MENU = [
  [t("common.entrees"), [[t("heroDemo.veloute_de_saison"), "9,00"], [t("heroDemo.oeuf_parfait_champignons"), "11,00"]]],
  [t("common.plats"), [[t("heroDemo.burger_du_chef"), "19,50"], [t("heroDemo.filet_de_bar_risotto"), "24,00"], [t("heroDemo.entrecote_frites"), "28,00"]]],
  [t("common.desserts"), [[t("heroDemo.tarte_tatin"), "8,50"], [t("heroDemo.cafe_gourmand"), "9,50"]]],
];
const BARS = [30, 55, 85, 100, 70, 40, 20];

function MenuCard() {
  return (
    <div className="hero-menu relative overflow-hidden rounded-xl bg-[#F6F2EA] px-3 py-3 text-[#2A231C] shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:px-6 sm:py-6">
      <p className="text-center font-serif text-[11px] font-semibold tracking-[0.2em] sm:text-base">{t("heroDemo.le_comptoir")}</p>
      <div className="mx-auto mt-1 h-px w-8 bg-[#2A231C]/30 sm:mt-2 sm:w-12" />
      <div className="mt-2 space-y-2 sm:mt-4 sm:space-y-3.5">
        {MENU.map(([cat, items], ci) => (
          <div key={cat}>
            <p className="font-serif text-[8px] italic text-[#9A5A2E] sm:text-xs">{cat}</p>
            {items.map(([n, p], i) => (
              <div key={n} className="hero-row mt-0.5 flex items-baseline gap-1 rounded px-0.5 font-serif text-[8px] sm:mt-1 sm:gap-2 sm:text-[13px]" style={{ animationDelay: `${(ci * 2 + i) * 0.45}s` }}>
                <span className="truncate">{n}</span>
                <span className="min-w-2 flex-1 border-b border-dotted border-[#2A231C]/25" />
                <span className="tabular-nums">{lang === "en" ? p.replace(",", ".") : p}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div aria-hidden className="hero-scan pointer-events-none absolute inset-0">
        <div className="h-12 -translate-y-full bg-gradient-to-b from-transparent to-emerald-500/20 sm:h-16" />
        <div className="h-px -translate-y-px bg-emerald-500 shadow-[0_0_12px_2px_rgba(16,185,129,0.6)]" />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, delay, testId }) {
  return (
    <div className="hero-pop rounded-lg border bg-background/60 p-1.5 sm:p-2.5" style={{ animationDelay: delay }} data-testid={testId}>
      <span className="flex items-center justify-between">
        <span className="num text-[12px] font-bold leading-tight sm:text-xl">{value}</span>
        <Icon className="h-2.5 w-2.5 text-emerald-500 sm:h-4 sm:w-4" />
      </span>
      <span className="mt-0.5 block text-[8px] leading-tight text-muted-foreground sm:text-xs">{label}</span>
    </div>
  );
}

function ResultsCard() {
  return (
    <div className="rounded-xl border bg-card/90 p-2.5 shadow-2xl shadow-emerald-950/30 backdrop-blur-xl sm:rounded-2xl sm:p-4">
      <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3">
        <p className="flex items-center gap-1.5 text-[9px] font-semibold sm:text-sm"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 sm:h-2 sm:w-2" />{t("heroDemo.analyse_terminee")}</p>
        <span className="rounded-full border px-1.5 py-0.5 text-[7px] uppercase tracking-wider text-muted-foreground sm:px-2 sm:text-[10px]" data-testid="hero-demo-badge">{t("heroDemo.exemple")}</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
        <Stat icon={UtensilsCrossed} value="24" label={t("common.plats_detectes")} delay="0.6s" testId="hero-demo-dishes" />
        <Stat icon={LayoutList} value="5" label={t("heroDemo.categories_detectees")} delay="0.8s" testId="hero-demo-categories" />
      </div>
      <div className="hero-pop mt-1.5 rounded-lg border bg-background/60 p-1.5 sm:mt-2 sm:p-2.5" style={{ animationDelay: "1s" }}>
        <p className="flex items-center justify-between text-[8px] text-muted-foreground sm:text-xs"><span className="flex items-center gap-1"><Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3" />{t("heroDemo.prix_analyses")}</span><Check className="h-2.5 w-2.5 text-emerald-500 sm:h-3.5 sm:w-3.5" /></p>
        <div className="mt-1.5 flex h-5 items-end gap-0.5 sm:mt-2 sm:h-10 sm:gap-1">
          {BARS.map((h, i) => <span key={i} className="hero-bar flex-1 rounded-sm bg-emerald-500/70" style={{ height: `${h}%`, animationDelay: `${1.1 + i * 0.06}s` }} />)}
        </div>
      </div>
      <div className="hero-pop mt-1.5 rounded-lg border border-[hsl(var(--ai)/0.35)] bg-[hsl(var(--ai)/0.08)] p-1.5 sm:mt-2 sm:p-2.5" style={{ animationDelay: "1.4s" }}>
        <p className="flex items-center gap-1 text-[8px] font-semibold text-[hsl(var(--ai))] sm:gap-1.5 sm:text-xs"><Sparkles className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />{t("heroDemo.3_opportunites_d_optimisation")}</p>
        <div className="mt-1 hidden flex-wrap gap-1 sm:mt-1.5 sm:flex">
          {[t("heroDemo.ancrage_des_prix"), t("heroDemo.descriptions"), t("heroDemo.equilibre_des_rubriques")].map((t) => <span key={t} className="rounded-md bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground">{t}</span>)}
        </div>
      </div>
    </div>
  );
}

export default function HeroDemo() {
  return (
    <figure className="relative mx-auto w-full max-w-[22rem] select-none sm:max-w-none" aria-label={t("heroDemo.illustration_du_fonctionnement_de_mymenupr")} data-testid="hero-demo">
      <div aria-hidden className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-[radial-gradient(ellipse_at_60%_40%,rgba(16,185,129,0.16),transparent_65%)]" />
      <div className="relative grid grid-cols-[46%_1fr] items-center gap-0">
        <div className="relative z-0 -rotate-2"><MenuCard /></div>
        <div className="relative z-10 -ml-[6%]">
          <div aria-hidden className="absolute -left-[16%] top-1/2 hidden h-px w-[16%] overflow-hidden bg-gradient-to-r from-emerald-500/0 to-emerald-500/60 sm:block">
            <span className="hero-beam block h-full w-1/3 bg-emerald-400" />
          </div>
          <div className="mb-1.5 flex items-center gap-1 pl-1 text-[8px] font-medium text-emerald-500 sm:mb-2 sm:gap-1.5 sm:text-xs"><ScanLine className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />{t("heroDemo.lecture_ia_de_la_carte")}</div>
          <ResultsCard />
        </div>
      </div>
      <figcaption className="mt-3 text-center text-[10px] text-muted-foreground/80 sm:mt-5 sm:text-xs">{t("heroDemo.illustration_exemple_d_analyse_pas_vos_don")}</figcaption>
    </figure>
  );
}
